import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { storageService, resizeToAspect } from '../services/storageService';
import { callRegistrarProveedorLogistica } from '../services/cloudFunctions';
import { ayudaPlaca, normalizarPlaca, revisarPlaca } from '../utils/placa';
import { validarTelefono } from '../utils/telefono';
import { mensajeDeError } from '../utils/mensajeDeError';
import { VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';
import { desgloseDeComision, TRAMOS_DE_COMISION, COMISION_MAXIMA } from '../constants/comisionLogistica';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ALTA DE UN VEHÍCULO DE LOGÍSTICA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El renglón que pidió el fundador el 5-oct-2026: «curier, mudanza o otro medio
 * que tiene que ver con vehículo más grande», y «que esa persona solo pague al
 * momento de adquirir el servicio».
 *
 * ── ESTO NO CAMBIA EL ROL DE NADIE ───────────────────────────────────────
 *
 * «que se acceda directamente desde el perfil que ya el usuario de pasajero
 * tenga creado». Así que esta pantalla NO llama a `setRole` ni a `setBaseRole`,
 * y el servidor tampoco toca `users/{uid}.role`. Se crea un documento aparte
 * (`proveedores_logistica/{uid}`) y la persona sigue siendo exactamente lo que
 * era. Si algún día esto empieza a escribir el rol, el dueño de la camioneta
 * deja de poder pedirse un viaje — que es justo lo contrario de lo que se pidió.
 *
 * ── Y NO SE ACERCA AL MOTOR DE VIAJES ────────────────────────────────────
 *
 * «no aplica lo que ya tenemos construido que es el minibús, de 6 o 5 pasajero
 * que ya lo tenemos bien, nada de eso se toca». El emparejamiento de viajes es
 * una sola consulta (`drivers` where `vehicleType` in {CAR, MOTORCYCLE,
 * MINIBUS}), así que los tipos de aquí viven en un espacio de nombres propio
 * —CAMIONETA, CAMION, VAN, AUTOBUS— y nada de esto escribe en `drivers`. El
 * motorista no va a recibir una solicitud de mudanza.
 *
 * ── LOS CUATRO PAPELES ───────────────────────────────────────────────────
 *
 * Son los que el fundador enumeró para validar a alguien: «foto del carro,
 * matrícula, licencia, seguro». Nace PENDIENTE y lo aprueba él, por la razón
 * que dio él mismo hablando del otro renglón: «cualquier usuario puede engañar
 * a nuestro mensajero con foto falsa si no cuenta con una verificación». Aquí
 * lo que está en juego es más grande todavía: alguien le entrega los muebles de
 * su casa a un desconocido.
 *
 * ── LAS CASILLAS NO SE LLENAN A LO LOCO ──────────────────────────────────
 *
 * El fundador ya vio una pantalla de este proyecto donde «las casillas se
 * pueden llenar con datos a lo loco y sigue avanzando, el número de teléfono
 * también pasa aunque ese número sea falso». Por eso aquí la placa pasa por
 * `revisarPlaca` y el teléfono por `validarTelefono` — las mismas utilidades que
 * se escribieron entonces — y el botón de enviar dice QUÉ falta en vez de
 * quedarse apagado sin explicación.
 */

type TipoVehiculo = 'CAMIONETA' | 'CAMION' | 'VAN' | 'AUTOBUS';
type Servicio = 'CURIER' | 'MUDANZA' | 'CARGA' | 'GIRA';

const VEHICULOS: { id: TipoVehiculo; nombre: string; icono: any; ayuda: string }[] = [
  { id: 'CAMIONETA', nombre: 'Camioneta', icono: 'car-outline', ayuda: 'Fletes, materiales, mudanzas pequeñas' },
  { id: 'CAMION', nombre: 'Camión', icono: 'bus-outline', ayuda: 'Mudanzas grandes y carga' },
  { id: 'VAN', nombre: 'Van / Furgón', icono: 'cube-outline', ayuda: 'Curier y paquetería' },
  { id: 'AUTOBUS', nombre: 'Autobús', icono: 'bus', ayuda: 'Giras y excursiones' },
];

const SERVICIOS: { id: Servicio; nombre: string; icono: any }[] = [
  { id: 'CURIER', nombre: 'Curier / paquetería', icono: 'cube' },
  { id: 'MUDANZA', nombre: 'Mudanza', icono: 'home' },
  { id: 'CARGA', nombre: 'Carga y materiales', icono: 'construct' },
  { id: 'GIRA', nombre: 'Autobús para gira', icono: 'people' },
];

/** Los cuatro papeles, en el orden en que el fundador los enumeró. */
const PAPELES = [
  { id: 'vehiculo', titulo: 'Foto del vehículo', ayuda: 'De frente, que se lea la placa' },
  { id: 'matricula', titulo: 'Matrícula', ayuda: 'La del vehículo que vas a usar' },
  { id: 'licencia', titulo: 'Licencia de conducir', ayuda: 'Vigente y de la categoría del vehículo' },
  { id: 'seguro', titulo: 'Seguro', ayuda: 'La póliza o el carnet del seguro' },
] as const;

type IdPapel = (typeof PAPELES)[number]['id'];

export default function LogisticaAltaScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);
  const nombreGuardado = useAppStore((s) => s.userName);
  const telefonoGuardado = useAppStore((s) => s.userPhone);

  const [tipoVehiculo, setTipoVehiculo] = React.useState<TipoVehiculo | null>(null);
  const [servicios, setServicios] = React.useState<Servicio[]>([]);
  const [nombre, setNombre] = React.useState(nombreGuardado ?? '');
  const [telefono, setTelefono] = React.useState(telefonoGuardado ?? '');
  const [placa, setPlaca] = React.useState('');
  const [capacidad, setCapacidad] = React.useState('');
  const [zonas, setZonas] = React.useState('');
  const [fotos, setFotos] = React.useState<Partial<Record<IdPapel, string>>>({});
  const [enviando, setEnviando] = React.useState(false);

  const avisar = (titulo: string, texto: string) => {
    // `Alert.alert` es un no-op en react-native-web: el botón parecería muerto.
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  const alternarServicio = (s: Servicio) =>
    setServicios((previos) => (previos.includes(s) ? previos.filter((x) => x !== s) : [...previos, s]));

  const tomarFoto = async (papel: IdPapel) => {
    try {
      if (Platform.OS !== 'web') {
        const { status } = await ImagePicker.requestCameraPermissionsAsync();
        if (status !== 'granted') {
          avisar('Permiso denegado', 'Necesitamos la cámara para que tomes la foto ahora mismo.');
          return;
        }
      }
      // En web no hay cámara del sistema; se cae a la galería para poder probar
      // la pantalla desde el navegador. En el teléfono, siempre cámara: una
      // foto vieja de un seguro vencido no prueba nada.
      const abrir =
        Platform.OS === 'web' ? ImagePicker.launchImageLibraryAsync : ImagePicker.launchCameraAsync;
      const r = await abrir({ mediaTypes: ['images'], quality: 0.85 });
      if (r.canceled || !r.assets?.[0]?.uri) return;
      // El ajuste de tamaño NO es cosmético: `launchCameraAsync` en un móvil de
      // 48 MP pasa el tope de 10 MB de Storage y la subida falla SIEMPRE, con el
      // mismo error para todos los intentos. Ya dejó a un usuario atrapado en el
      // primer paso del registro.
      const ajustada = await resizeToAspect(r.assets[0].uri, 1600, 4, 3);
      setFotos((previas) => ({ ...previas, [papel]: ajustada }));
    } catch (e: any) {
      avisar('No pudimos tomar la foto', mensajeDeError(e, 'Inténtalo otra vez.'));
    }
  };

  /**
   * Qué falta, en una frase. Antes de escribir esto el botón se quedaba
   * apagado y la persona no sabía por qué — y eso es exactamente lo que el
   * fundador llamó «el usuario queda atrapado».
   */
  const loQueFalta = (): string | null => {
    if (!tipoVehiculo) return 'Elige qué vehículo tienes.';
    if (servicios.length === 0) return 'Marca al menos un servicio que quieras dar.';
    if (!nombre.trim()) return 'Escribe tu nombre.';
    const tel = validarTelefono(telefono, 'personal');
    if (tel.ok === false) return tel.motivo;
    const rev = revisarPlaca(placa, false);
    if (rev.ok === false) return rev.motivo;
    if (!capacidad.trim()) return 'Dinos cuánto carga o cuánta gente lleva.';
    const sinFoto = PAPELES.find((p) => !fotos[p.id]);
    if (sinFoto) return `Falta la foto: ${sinFoto.titulo.toLowerCase()}.`;
    return null;
  };

  const enviar = async () => {
    if (enviando) return;
    if (!uid) {
      avisar('Necesitas tu cuenta', 'Entra con tu cuenta para registrar tu vehículo.');
      return;
    }
    const falta = loQueFalta();
    if (falta) {
      avisar('Falta un dato', falta);
      return;
    }
    // `loQueFalta()` ya devolvió null, así que las dos revisiones pasan. Se
    // repiten aquí porque son las que dan el valor NORMALIZADO que se envía —
    // y porque el día que alguien añada un camino que salte la comprobación,
    // este `return` es el que evita mandar basura al servidor.
    const rev = revisarPlaca(placa, false);
    const tel = validarTelefono(telefono, 'personal');
    if (rev.ok === false || tel.ok === false) {
      setEnviando(false);
      avisar('Falta un dato', loQueFalta() ?? 'Revisa la placa y el teléfono.');
      return;
    }
    setEnviando(true);
    try {
      // Las cuatro fotos van a `kyc_documents/{uid}/`: las escribe solo el dueño
      // y las leen el dueño y los admins. Es la carpeta que ya usa el KYC, con
      // su tope de 10 MB y sus reglas puestas.
      const urls: Record<string, string> = {};
      for (const p of PAPELES) {
        urls[p.id] = await storageService.uploadKYCDocument(uid, `logistica_${p.id}`, fotos[p.id]!, null);
      }

      await callRegistrarProveedorLogistica({
        tipoVehiculo: tipoVehiculo!,
        servicios,
        nombre: nombre.trim(),
        telefono: tel.digitos,
        placa: rev.placa,
        capacidad: capacidad.trim(),
        zonas: zonas.trim(),
        fotoVehiculoUrl: urls.vehiculo,
        fotoMatriculaUrl: urls.matricula,
        fotoLicenciaUrl: urls.licencia,
        fotoSeguroUrl: urls.seguro,
      });

      avisar(
        'Tu vehículo quedó enviado',
        'Vamos a revisar tus papeles y la foto del vehículo. Te avisamos en cuanto esté aprobado. Mientras tanto puedes seguir usando la app como siempre.'
      );
      router.replace('/logistica' as any);
    } catch (e: any) {
      avisar('No pudimos enviarlo', mensajeDeError(e, 'Revisa tu conexión e inténtalo otra vez.'));
    } finally {
      setEnviando(false);
    }
  };

  // El candado de RUTA. Esconder la tarjeta no basta: la URL de expo-router
  // sigue existiendo, y una pantalla que promete un renglón que todavía no
  // cobra es la clase de promesa sin pantalla por la que ya nos rechazaron.
  if (!VERTICAL_LOGISTICA_VISIBLE) return null;

  const falta = loQueFalta();

  return (
    <ScreenBackground>
      <PantallaSegura>
        <View style={[estilos.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={estilos.salida}>
            <Ionicons name="chevron-back" size={22} color={colors.text} />
            <Text style={[estilos.salidaTexto, { color: colors.text }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={estilos.cuerpo} keyboardShouldPersistTaps="handled">
          <Text style={[estilos.titulo, { color: colors.text }]}>Registra tu vehículo de carga</Text>
          <Text style={[estilos.bajada, { color: colors.textSecondary }]}>
            Curier, mudanza, carga y autobús para gira. No pagas mensualidad: solo una comisión
            por el servicio que realices.
          </Text>

          {/* ── QUÉ VEHÍCULO ─────────────────────────────────────────── */}
          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>TU VEHÍCULO</Text>
          {VEHICULOS.map((v) => {
            const elegido = tipoVehiculo === v.id;
            return (
              <TouchableOpacity
                key={v.id}
                onPress={() => setTipoVehiculo(v.id)}
                style={[
                  estilos.opcion,
                  {
                    backgroundColor: colors.card,
                    borderColor: elegido ? colors.primary : colors.border,
                    borderWidth: elegido ? 2 : 1,
                  },
                ]}
              >
                <Ionicons name={v.icono} size={24} color={elegido ? colors.primary : colors.textSecondary} />
                <View style={estilos.opcionTexto}>
                  <Text style={[estilos.opcionTitulo, { color: colors.text }]}>{v.nombre}</Text>
                  <Text style={[estilos.opcionAyuda, { color: colors.textSecondary }]}>{v.ayuda}</Text>
                </View>
                {elegido && <Ionicons name="checkmark-circle" size={22} color={colors.primary} />}
              </TouchableOpacity>
            );
          })}

          {/* ── QUÉ SERVICIOS ────────────────────────────────────────── */}
          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>QUÉ SERVICIOS DAS</Text>
          <Text style={[estilos.ayudaSeccion, { color: colors.textSecondary }]}>
            Marca todos los que hagas. Solo verás los trabajos que encajen con lo que marques.
          </Text>
          {SERVICIOS.map((s) => {
            const marcado = servicios.includes(s.id);
            return (
              <TouchableOpacity
                key={s.id}
                onPress={() => alternarServicio(s.id)}
                style={[
                  estilos.opcion,
                  {
                    backgroundColor: colors.card,
                    borderColor: marcado ? colors.primary : colors.border,
                    borderWidth: marcado ? 2 : 1,
                  },
                ]}
              >
                <Ionicons name={s.icono} size={22} color={marcado ? colors.primary : colors.textSecondary} />
                <View style={estilos.opcionTexto}>
                  <Text style={[estilos.opcionTitulo, { color: colors.text }]}>{s.nombre}</Text>
                </View>
                <Ionicons
                  name={marcado ? 'checkbox' : 'square-outline'}
                  size={22}
                  color={marcado ? colors.primary : colors.textSecondary}
                />
              </TouchableOpacity>
            );
          })}

          {/* ── TUS DATOS ────────────────────────────────────────────── */}
          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>TUS DATOS</Text>
          <TextInput
            value={nombre}
            onChangeText={setNombre}
            placeholder="Tu nombre y apellido"
            placeholderTextColor={colors.textSecondary}
            maxLength={80}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <TextInput
            value={telefono}
            onChangeText={setTelefono}
            placeholder="Teléfono con WhatsApp"
            placeholderTextColor={colors.textSecondary}
            keyboardType="phone-pad"
            maxLength={20}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <TextInput
            value={placa}
            onChangeText={(v) => setPlaca(normalizarPlaca(v))}
            placeholder={ayudaPlaca(false)}
            placeholderTextColor={colors.textSecondary}
            autoCapitalize="characters"
            maxLength={10}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <TextInput
            value={capacidad}
            onChangeText={setCapacidad}
            placeholder="Cuánto carga (ej: 2 toneladas, 30 pasajeros)"
            placeholderTextColor={colors.textSecondary}
            maxLength={60}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />
          <TextInput
            value={zonas}
            onChangeText={setZonas}
            placeholder="Zonas donde trabajas (opcional)"
            placeholderTextColor={colors.textSecondary}
            maxLength={200}
            style={[estilos.campo, { backgroundColor: colors.card, borderColor: colors.border, color: colors.text }]}
          />

          {/* ── LOS CUATRO PAPELES ───────────────────────────────────── */}
          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>TUS PAPELES</Text>
          <Text style={[estilos.ayudaSeccion, { color: colors.textSecondary }]}>
            Alguien de Go Safely los va a revisar con sus propios ojos. Le estás pidiendo a una
            persona que te confíe sus cosas: esto es lo que nos deja responder por ti.
          </Text>
          {PAPELES.map((p) => {
            const puesta = fotos[p.id];
            return (
              <TouchableOpacity
                key={p.id}
                onPress={() => tomarFoto(p.id)}
                style={[estilos.papel, { backgroundColor: colors.card, borderColor: puesta ? '#10B981' : colors.border }]}
              >
                {puesta ? (
                  <Image source={{ uri: puesta }} style={estilos.miniatura} />
                ) : (
                  <View style={[estilos.miniatura, estilos.miniaturaVacia, { borderColor: colors.border }]}>
                    <Ionicons name="camera-outline" size={22} color={colors.textSecondary} />
                  </View>
                )}
                <View style={estilos.opcionTexto}>
                  <Text style={[estilos.opcionTitulo, { color: colors.text }]}>{p.titulo}</Text>
                  <Text style={[estilos.opcionAyuda, { color: colors.textSecondary }]}>
                    {puesta ? 'Toca para cambiarla' : p.ayuda}
                  </Text>
                </View>
                {puesta && <Ionicons name="checkmark-circle" size={22} color="#10B981" />}
              </TouchableOpacity>
            );
          })}

          {/* ── LO QUE SE COBRA, ANTES DE FIRMAR ─────────────────────── */}
          <Text style={[estilos.rotulo, { color: colors.textSecondary }]}>LO QUE SE COBRA</Text>
          <View style={[estilos.tarifa, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Text style={[estilos.tarifaTitulo, { color: colors.text }]}>
              Sin mensualidad. Solo una comisión por trabajo hecho.
            </Text>
            {TRAMOS_DE_COMISION.map((tramo, i) => {
              const desde = i === 0 ? 0 : (TRAMOS_DE_COMISION[i - 1].hasta ?? 0);
              return (
                <Text key={i} style={[estilos.tarifaLinea, { color: colors.textSecondary }]}>
                  {tramo.hasta === null
                    ? `· Lo que pase de RD$${desde.toLocaleString('es-DO')}: ${tramo.porciento}%`
                    : i === 0
                      ? `· Hasta RD$${tramo.hasta.toLocaleString('es-DO')}: ${tramo.porciento}%`
                      : `· De RD$${(desde + 1).toLocaleString('es-DO')} a RD$${tramo.hasta.toLocaleString('es-DO')}: ${tramo.porciento}%`}
                </Text>
              );
            })}
            <Text style={[estilos.tarifaLinea, { color: colors.textSecondary }]}>
              {`· Nunca más de RD$${COMISION_MAXIMA.toLocaleString('es-DO')} por servicio.`}
            </Text>
            {/* Un ejemplo concreto vale más que la tabla: es lo que la persona
                usa para decidir. */}
            <Text style={[estilos.tarifaEjemplo, { color: colors.text }]}>
              Ejemplo: una mudanza de RD$10,000 deja RD$
              {desgloseDeComision(10000).comision.toLocaleString('es-DO')} de comisión. Tú recibes RD$
              {desgloseDeComision(10000).recibe.toLocaleString('es-DO')}.
            </Text>
          </View>

          {/* El botón NO se apaga: dice qué falta. Un botón apagado sin
              explicación es lo que el fundador llamó «el usuario queda
              atrapado». */}
          {falta && <Text style={[estilos.falta, { color: '#D97706' }]}>{falta}</Text>}
          <TouchableOpacity
            onPress={enviar}
            disabled={enviando}
            style={[estilos.enviar, { backgroundColor: falta ? colors.border : colors.primary }]}
          >
            {enviando ? (
              <ActivityIndicator color="#FFF" />
            ) : (
              <Text style={[estilos.enviarTexto, { color: falta ? colors.textSecondary : '#FFF' }]}>
                Enviar para revisión
              </Text>
            )}
          </TouchableOpacity>
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  cabecera: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  salida: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4 },
  salidaTexto: { fontSize: 16, marginLeft: 2 },
  cuerpo: { paddingHorizontal: 16, paddingTop: 8 },
  titulo: { fontSize: 24, fontWeight: '700', marginBottom: 6 },
  bajada: { fontSize: 14, lineHeight: 20, marginBottom: 8 },
  rotulo: { fontSize: 12, fontWeight: '700', letterSpacing: 0.5, marginTop: 22, marginBottom: 8 },
  ayudaSeccion: { fontSize: 13, lineHeight: 19, marginBottom: 10 },
  opcion: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, padding: 14, marginBottom: 8, gap: 12 },
  opcionTexto: { flex: 1 },
  opcionTitulo: { fontSize: 15, fontWeight: '600' },
  opcionAyuda: { fontSize: 12, marginTop: 2 },
  campo: { borderRadius: 12, borderWidth: 1, paddingHorizontal: 14, paddingVertical: 12, fontSize: 15, marginBottom: 8 },
  papel: { flexDirection: 'row', alignItems: 'center', borderRadius: 12, borderWidth: 1, padding: 12, marginBottom: 8, gap: 12 },
  miniatura: { width: 56, height: 42, borderRadius: 8 },
  miniaturaVacia: { alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderStyle: 'dashed' },
  tarifa: { borderRadius: 12, borderWidth: 1, padding: 14 },
  tarifaTitulo: { fontSize: 14, fontWeight: '700', marginBottom: 8 },
  tarifaLinea: { fontSize: 13, lineHeight: 20 },
  tarifaEjemplo: { fontSize: 13, lineHeight: 19, marginTop: 10, fontWeight: '600' },
  falta: { fontSize: 13, marginTop: 18, marginBottom: 4, fontWeight: '600' },
  enviar: { borderRadius: 14, paddingVertical: 16, alignItems: 'center', marginTop: 10 },
  enviarTexto: { fontSize: 16, fontWeight: '700' },
});
